import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'; // Import CardHeader, CardTitle
import { getAuth } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { reportService } from '../services/reportService';
import { invoiceService } from '../services/invoiceService'; // Corrected path (removed one ../)
import { useToast } from '../hooks/use-toast';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { LoadingSpinner } from './ui/loading-spinner'; // Import LoadingSpinner
import { useTheme } from './ui/theme-provider';
import { Label } from './ui/label';
import { cn } from '../lib/utils';
import { format, isPast, differenceInDays, isValid, startOfDay, endOfDay, startOfWeek, startOfMonth, endOfMonth, subMonths, addDays } from 'date-fns'; // Ensure all needed functions are imported
import queryString from 'query-string';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table"; // Corrected import path (removed .jsx)
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Eye, Mail, Trash2, RotateCcw, RefreshCw } from 'lucide-react'; // Import Mail, Eye, RotateCcw
import { Toaster } from './ui/toaster';
import HelpIcon from './ui/HelpIcon';
import { ROUTES } from '../App'; // Import ROUTES

// Define default filters outside the component - SIMPLIFIED to always use current month
const defaultFilters = {
  practice: 'all',
  startDate: startOfMonth(new Date()),
  endDate: endOfMonth(new Date()), // Always use full current month
  period: 'this-month',
  showDeleted: false,
  statusFilter: 'all',
};

// Helper function to load filters from sessionStorage - REVERTED TO ORIGINAL LOGIC
const loadFiltersFromSession = () => {
  try {
    const savedFilters = sessionStorage.getItem('invoiceHistoryFilters');
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      // Ensure date strings are converted back to Date objects
      if (parsed.startDate) parsed.startDate = new Date(parsed.startDate);
      if (parsed.endDate) parsed.endDate = new Date(parsed.endDate);
      if (process.env.NODE_ENV === 'development') {
        console.log('[InvoiceHistory] Loaded filters from sessionStorage:', parsed);
      }
      return parsed;
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[InvoiceHistory] Error loading filters from sessionStorage:', e);
    }
  }
  if (process.env.NODE_ENV === 'development') {
    console.log('[InvoiceHistory] No filters found in sessionStorage, using defaults.');
  }
  // return defaultFilters; // Always return default filters // REVERTED
  return defaultFilters; // Fallback to defaults if nothing loaded or error
};

// Helper function to parse dates from invoice summary entries
const parseEntryDate = (entry) => {
  if (!entry) return null;

  if (entry.date?.seconds) {
    // Firestore Timestamp
    try {
      const date = new Date(entry.date.seconds * 1000);
      if (!isNaN(date.getTime())) return date;
    } catch (e) { /* ignore */ }
  } else if (entry.date) {
    // Could be ISO string or already a Date object
    try {
      const date = new Date(entry.date);
      if (!isNaN(date.getTime())) return date;
    } catch (e) { /* ignore */ }
  } else if (entry.formattedDate && typeof entry.formattedDate === 'string') {
    // Custom format "dd MMM yyyy" or similar
    try {
      const parts = entry.formattedDate.split(' ');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        // More robust month mapping
        const monthMap = {
          'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
          'Jul': 6, 'Aug': 7, 'Sep': 8, 'Sept': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };
        const monthStr = parts[1]?.substring(0, 3); // Use first 3 chars for matching
        const month = monthMap[monthStr];
        const year = parseInt(parts[2], 10);
        if (!isNaN(day) && month !== undefined && !isNaN(year)) {
          const date = new Date(year, month, day);
           if (!isNaN(date.getTime())) return date;
        }
      } else {
        // Fallback for other string formats Date might parse
         const date = new Date(entry.formattedDate);
         if (!isNaN(date.getTime())) return date;
      }
    } catch (e) { /* ignore */ }
  }
  // console.warn('Could not parse date from entry:', entry); // Optional: Log if parsing fails
  return null;
};


const InvoiceHistory = () => {
  // SIMPLIFIED: Always start with fresh defaults - no sessionStorage loading
  const [filters, setFilters] = useState(defaultFilters);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading data...');
  const [practices, setPractices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  // Initialize UI state to defaults
  const [fromReports, setFromReports] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const initialUrlProcessingDone = useRef(false); // Ref to track initial processing
  const [viewingReportMonth, setViewingReportMonth] = useState(false);
  const [reportMonth, setReportMonth] = useState(null);
  const [recentlyDeletedIds, setRecentlyDeletedIds] = useState([]);
  const [recentlyRestoredIds, setRecentlyRestoredIds] = useState([]);
  // Add state for summary totals
  const [summaryTotals, setSummaryTotals] = useState({ paid: 0, unpaid: 0, overdue: 0, count: 0 });
  const [updatingPaymentId, setUpdatingPaymentId] = useState(null); // State to track which checkbox is updating
  const { theme } = useTheme(); // Get theme

  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Ensure location is available
  const fetchingRef = React.useRef(false);
  const { toast } = useToast();
  const [error, setError] = useState(null); // Ensure error state is defined
  // SIMPLIFIED: Always force fresh fetch on initial load for current month
  const fetchFilteredInvoices = useCallback((options = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Using filtered invoice fetch with filters:', {
        practice: filters.practice,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
      }, 'Options:', options);
    }

    if (!auth.currentUser?.uid) {
      setLoading(false);
      return Promise.resolve([]);
    }

    // SIMPLIFIED: For current month (initial load), always force fresh fetch
    const isCurrentMonth = filters.period === 'this-month' && 
                          filters.startDate?.getMonth() === new Date().getMonth() &&
                          filters.startDate?.getFullYear() === new Date().getFullYear();
      const fetchOptions = isCurrentMonth ? { forceRefresh: true, ...options } : options;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[InvoiceHistory] Fetching data. Current month: ${isCurrentMonth}, Force refresh: ${fetchOptions.forceRefresh}`);
    }

    return reportService.getAllInvoiceData(
      auth.currentUser.uid,
      filters.practice,
      filters.startDate,
      filters.endDate,
      fetchOptions
    )      .then(result => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[InvoiceHistory] Raw data received from service:`, result.length, 'invoices');
        }
        setAllInvoices(result);
        setLoading(false);
        return result;
      })      .catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error in filtered fetch:', error);
        }
        setLoading(false);
        setError('Failed to load invoices.');
        return [];
      });
  }, [auth.currentUser?.uid, filters.practice, filters.startDate, filters.endDate, filters.period]);

  // fetchSpecificInvoices now comes AFTER fetchFilteredInvoices
  // NOTE: You might also need to update fetchSpecificInvoices if it uses reportService methods
  // that accept the options object (like getAllInvoiceData or getInvoiceById with force options).
  // For now, focusing on the main list fetch via fetchFilteredInvoices.
  const fetchSpecificInvoices = useCallback((invoiceIdsParam, idStorageKey, options = {}) => { // Added options param
    let invoiceIds = [];    if (invoiceIdsParam) {
      invoiceIds = invoiceIdsParam.split(',');
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching ${invoiceIds.length} specific invoices from URL parameter`);
      }
    } else if (idStorageKey) {
      try {
        const storedIds = localStorage.getItem(idStorageKey);        if (storedIds) {
          invoiceIds = JSON.parse(storedIds);
          if (process.env.NODE_ENV === 'development') {
            console.log(`Fetched ${invoiceIds.length} invoice IDs from localStorage key: ${idStorageKey}`);
          }
          localStorage.removeItem(idStorageKey);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`No invoice IDs found in localStorage for key: ${idStorageKey}`);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error retrieving invoice IDs from localStorage:', error);
        }
      }
    }

    if (invoiceIds.length > 0) {
      setLoadingMessage(`Loading ${invoiceIds.length} specific invoices...`);
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Fetching specific invoices with options:`, options); // Log options
      }

      // Assuming getInvoicesByIds internally calls getInvoiceById which might accept options
      // Or adjust if getInvoicesByIds itself needs the forceRefresh option
      const fetchPromises = invoiceIds.map(id =>
        reportService.getInvoiceById(auth.currentUser.uid, id, { source: options.forceRefresh ? 'server' : 'cache' }) // Example: Force server fetch if needed
      );

      return Promise.all(fetchPromises)
        .then(results => {
          const validResults = results.filter(Boolean); // Filter out nulls if getInvoiceById returns null for not found
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Successfully fetched ${validResults.length} specific invoices`);
          }
          setAllInvoices(validResults);
          setLoading(false);
          return validResults;
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.error(`Error fetching specific invoices:`, error);
          }
          setLoading(false);
          setError('Failed to load specific invoices.'); // Set error message
          // Fallback now correctly uses fetchFilteredInvoices which is defined above
          return fetchFilteredInvoices(options); // Pass options to fallback
        });
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.log("No invoice IDs found, falling back to standard fetch");
      }
      // Fallback now correctly uses fetchFilteredInvoices which is defined above
      return fetchFilteredInvoices(options); // Pass options to fallback
    }
  // Ensure auth.currentUser.uid is stable or included if needed
  // Added fetchFilteredInvoices dependency because it's used in fallbacks
  }, [auth.currentUser?.uid, fetchFilteredInvoices]);

  // Refined dependencies for refreshInvoices
  // ADD options argument
  const refreshInvoices = useCallback((options = {}) => {
    // Log the received force parameter from the options object
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[InvoiceHistory] Refreshing invoices... Force: ${options?.force}`);
    }
    if (!auth.currentUser?.uid) {
      if (process.env.NODE_ENV !== 'production') {
        console.log("[InvoiceHistory] No user ID, cannot refresh.");
      }
      return;
    }

    setLoading(true);
    setLoadingMessage('Loading data...');
    // Intentionally NOT resetting allInvoices here, let the fetch replace it

    const queryParams = queryString.parse(location.search);
    const invoiceIdsParam = queryParams.invoiceIds;
    const idStorageKey = queryParams.idStorageKey;

    // Create the options object to pass down, using the force value from the input options
    const fetchOptions = { forceRefresh: options?.force };
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[InvoiceHistory] Fetching with options:`, fetchOptions);
    }

    const fetchPromise = invoiceIdsParam || idStorageKey
      // Pass fetchOptions as the third argument to fetchSpecificInvoices
      ? fetchSpecificInvoices(invoiceIdsParam, idStorageKey, fetchOptions)
      // Pass fetchOptions as the first argument to fetchFilteredInvoices (adjust if it takes more args)
      // Assuming fetchFilteredInvoices signature is fetchFilteredInvoices(options) based on previous context
      : fetchFilteredInvoices(fetchOptions);

    fetchPromise.finally(() => {
      fetchingRef.current = false;
      // setLoading(false); // Consider moving setLoading(false) inside the fetch functions' success/error handlers
    });
  // Dependencies: fetch functions, location search (for specific IDs), user ID
  }, [location.search, auth.currentUser?.uid, fetchSpecificInvoices, fetchFilteredInvoices]);
  

  // MOVED calculateDateRange UP and MODIFIED
  const calculateDateRange = useCallback((period) => {
    const today = new Date();
    let start, end;

    switch (period) {
      case 'today':
        start = startOfDay(today); // Use imported function
        end = endOfDay(today); // Use imported function
        break;
      case 'this-week':
        start = startOfWeek(today, { weekStartsOn: 1 }); // Use imported function
        end = endOfDay(today); // Use imported function
        break;
      case 'this-month':
        start = startOfMonth(today); // Use imported function
        // Use end of the current month for "this-month" to include all items created this month
        end = endOfMonth(today); // Use imported function - changed from endOfDay(today)
        break;
      case 'last-month':
        const lastMonth = subMonths(today, 1); // Use imported function
        start = startOfMonth(lastMonth); // Use imported function
        end = endOfMonth(lastMonth); // Use imported function
        break;
      // *** ADD Last 3 Months ***
      case 'last-3-months':
        start = startOfMonth(subMonths(today, 3)); // Use imported functions
        end = endOfMonth(subMonths(today, 1)); // Use imported functions
        break;
      // *** ADD Last 6 Months ***
      case 'last-6-months':
        start = startOfMonth(subMonths(today, 6)); // Use imported functions
        end = endOfMonth(subMonths(today, 1)); // Use imported functions
        break;
      case 'all-time':
      default:
        start = null; // Or a very early date if needed
        end = null;   // Or a very future date if needed
        break;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`[calculateDateRange] Period: ${period}, Start: ${start}, End: ${end}`);
    }
    return { startDate: start, endDate: end };
  }, []);


  // Filter change handlers remain the same (only updates filters state)
  const handleFilterPeriodChange = useCallback((newPeriod) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Period changed from ${filters.period} to ${newPeriod}`);
    }
    const { startDate, endDate } = calculateDateRange(newPeriod); // Now defined above

    setFilters(prev => ({
      ...prev,
      period: newPeriod,
      startDate: startDate,
      endDate: endDate,
      forceRefresh: true // <<< FORCE REFRESH ON PERIOD CHANGE
    }));
  }, [calculateDateRange, filters.period]); // Added filters.period

  const handlePracticeChange = useCallback((newPractice) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Practice changed from ${filters.practice} to ${newPractice}`);
    }
    setFilters(prevFilters => ({
      ...prevFilters,
      practice: newPractice
    }));
  }, []); // Removed filters.practice dependency

  const handleShowDeletedChange = useCallback((showDeleted) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Show deleted changed to ${showDeleted}`);
    }
    setFilters(prevFilters => ({
      ...prevFilters,
      showDeleted
    }));
  }, []);

  // Added handler for status filter change
  const handleStatusFilterChange = useCallback((newStatus) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Status filter changed to ${newStatus}`);
    }
    setFilters(prevFilters => ({
      ...prevFilters,
      statusFilter: newStatus
    }));
  }, []);

  // Effect to process URL parameters on EVERY relevant navigation change.
  // Sets UI state flags AND resets filters when exiting drilldown.
  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[InvoiceHistory] Processing URL query parameters:', queryParams);
    }

    const isMonthView = !!queryParams.month;
    const isSpecificIdView = !!queryParams.invoiceIds || !!queryParams.idStorageKey;
    const isDrilldown = isMonthView || isSpecificIdView;
    const isTreatmentDateView = queryParams.viewMode === 'treatmentDate';

    // Always update UI state based on current URL
    setViewingReportMonth(isMonthView);
    setReportMonth(isMonthView ? queryParams.month : null);
    setFromReports(isTreatmentDateView); // Determine if navigating back to reports is needed
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[InvoiceHistory] Setting UI state: viewingReportMonth=${isMonthView}, fromReports=${isTreatmentDateView}`);
    }

    // Reset filters if navigating TO a non-drilldown view
    if (!isDrilldown) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[InvoiceHistory] Normal navigation detected. Ensuring filters match session/defaults.');
        }
        const sessionOrDefaults = loadFiltersFromSession();
        // Only update if filters actually changed to prevent potential loops
        setFilters(currentFilters => {
            // Basic string comparison; consider a deep comparison library if filters become complex
            if (JSON.stringify(currentFilters) !== JSON.stringify(sessionOrDefaults)) {
                if (process.env.NODE_ENV !== 'production') {
                  console.log('[InvoiceHistory] Resetting filters state to session/defaults.');
                }
                return sessionOrDefaults;
            }
            if (process.env.NODE_ENV !== 'production') {
              console.log('[InvoiceHistory] Filters state already matches session/defaults. No reset needed.');
            }
            return currentFilters; // No change
        });
    } else {
        // Handle potential practice override during drilldown
        if (queryParams.practice) {
          setFilters(prev => {
            if (prev.practice !== queryParams.practice) {
              if (process.env.NODE_ENV !== 'production') {
                console.log('[InvoiceHistory] Applying practice override from URL for drilldown:', queryParams.practice);
              }
              return { ...prev, practice: queryParams.practice };
            }
            return prev; // No change needed
          });
        }
        // Note: Period filter state is not explicitly overridden here for month view,
        // as the dropdown is hidden. The underlying state might persist but isn't visible.
    }

    // Mark initial load as complete *once* using the ref
    if (!initialUrlProcessingDone.current) {
        setInitialLoadComplete(true); // Trigger the initial data fetch effect
        initialUrlProcessingDone.current = true;
        if (process.env.NODE_ENV !== 'production') {
          console.log('[InvoiceHistory] Initial URL processing complete. initialLoadComplete set to true.');
        }
    }

  // Dependency: Run whenever the URL search string changes.
  }, [location.search]);
  

  // Effect to SAVE filters to sessionStorage (Refined drilldown check)
  useEffect(() => {
    // SIMPLIFIED: No more sessionStorage filter persistence
    if (process.env.NODE_ENV !== 'production') {
      console.log('[InvoiceHistory] Filter persistence disabled for simplified approach');
    }
  }, [filters, initialLoadComplete, location.search]);

  // Effect to trigger refreshInvoices when relevant filters change OR on initial load completion
  useEffect(() => {
    // Only refresh if initial load/URL processing is complete
    if (initialLoadComplete) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Data fetch trigger: initialLoadComplete is true. Refreshing invoices based on current filters/URL.');
      }
      // refreshInvoices internally checks URL params vs filters state
      refreshInvoices();
    }
  // This effect now runs once after initial load, and whenever refreshInvoices function identity changes.
  // The actual data fetching logic inside refreshInvoices depends on filters state and location.search
  }, [initialLoadComplete, refreshInvoices]);

  const filterInvoicesByMonth = useCallback((invoices, monthStr) => {
    if (!monthStr) return invoices;

    if (process.env.NODE_ENV !== 'production') {
      console.log('Filtering invoices by month:', monthStr);
    }

    const monthYearParts = monthStr.split(' ');
    if (monthYearParts.length !== 2) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Invalid month format:', monthStr);
      }
      return invoices;
    }

    // Corrected month normalization: Use first 3 letters, capitalized
    let targetMonth = monthYearParts[0].substring(0, 3);
    targetMonth = targetMonth.charAt(0).toUpperCase() + targetMonth.slice(1).toLowerCase();
    const targetYear = parseInt(monthYearParts[1], 10);

    if (process.env.NODE_ENV !== 'production') {
      console.log(`Normalized target: Month=${targetMonth}, Year=${targetYear}`);
    }

    return invoices.filter(invoice => {
      const params = new URLSearchParams(location.search);
      const viewMode = params.get('viewMode');
      
      if (viewMode === 'treatmentDate') {
        return invoice.summaryData?.some(entry => {
          let treatmentDate;
          
          if (entry.date?.seconds) {
            treatmentDate = new Date(entry.date.seconds * 1000);
          } else if (entry.date) {
            treatmentDate = new Date(entry.date);
          } else if (entry.formattedDate) {
            try {
              const dateParts = entry.formattedDate.split(' ');
              if (dateParts.length === 3) {
                const day = parseInt(dateParts[0], 10);
                const monthMap = { 
                  'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                  'Jul': 6, 'Aug': 7, 'Sep': 8, 'Sept': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 
                };
                const fullMonthMap = {
                  'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
                  'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
                };
                
                let month = monthMap[dateParts[1]];
                if (month === undefined) {
                  month = fullMonthMap[dateParts[1]];
                }
                
                const year = parseInt(dateParts[2], 10);
                
                if (!isNaN(day) && month !== undefined && !isNaN(year)) {
                  treatmentDate = new Date(year, month, day);
                }
              } else {
                treatmentDate = new Date(entry.formattedDate);
              }
            } catch (error) {
              console.error('Error parsing formatted date:', entry.formattedDate, error);
            }
          }

          if (!treatmentDate || isNaN(treatmentDate.getTime())) {
            return false;
          }

          // Ensure treatmentMonth is also short format for comparison
          const treatmentMonth = treatmentDate.toLocaleString('en-US', { month: 'short' });
          const treatmentYear = treatmentDate.getFullYear();
          
          // Compare short month names
          return treatmentMonth.toLowerCase() === targetMonth.toLowerCase() && 
                 treatmentYear === targetYear;
        });
      } else {
        if (!invoice.createdAt?.seconds) {
          return false;
        }
        
        const invoiceDate = new Date(invoice.createdAt.seconds * 1000);
        // Ensure invoiceMonth is also short format for comparison
        const invoiceMonth = invoiceDate.toLocaleString('en-US', { month: 'short' });
        const invoiceYear = invoiceDate.getFullYear();
        
        // Compare short month names
        return invoiceMonth.toLowerCase() === targetMonth.toLowerCase() && 
               invoiceYear === targetYear;
      }
    });
  }, [location.search]);

  const getFilteredPractices = useCallback((invoicesList) => {
    const practiceSet = new Set();
    invoicesList.forEach(invoice => {
      if (invoice.practiceDetails?.prac_name && invoice.practiceId) {
        practiceSet.add(JSON.stringify({
          id: invoice.practiceId,
          name: invoice.practiceDetails.prac_name
        }));
      }
    });
    return Array.from(practiceSet).map(p => JSON.parse(p))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const updatePractices = useCallback((invoices) => {
    const availablePractices = getFilteredPractices(invoices);
    setPractices(availablePractices);
  }, [getFilteredPractices]);

  // MOVED getPaymentStatusStyle function definition UP
  // Fix the getPaymentStatusStyle function to avoid using 'arguments' in arrow function
  const getPaymentStatusStyle = useCallback((invoiceOrIsPaid, maybeIsOverdue) => {
    // Handle different function signatures for backward compatibility
    let isPaid, isOverdue;
    
    if (typeof invoiceOrIsPaid === 'boolean') {
      // Old signature: (isPaid, isOverdue)
      isPaid = invoiceOrIsPaid;
      isOverdue = maybeIsOverdue;
    } else {
      // New signature: (invoice)
      const invoice = invoiceOrIsPaid;
      isPaid = invoice.isPaid;
      isOverdue = invoice.isOverdue || calculateIsOverdue(invoice);
    }
    
    const baseClasses = "text-xs px-2 py-0.5 rounded-full border"; // Base classes for all badges
  
    if (isPaid) {
      return cn(baseClasses, theme === 'light' ? 'bg-green-100 border-green-300 text-green-800' : 'bg-green-900/30 border-green-500/50 text-green-300');
    } else if (isOverdue) {
      return cn(baseClasses, theme === 'light' ? 'bg-red-100 border-red-300 text-red-800' : 'bg-red-900/30 border-red-500/50 text-red-300');
    } else { // Unpaid but not overdue
      return cn(baseClasses, theme === 'light' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' : 'bg-yellow-900/30 border-yellow-500/50 text-yellow-300');
    }
  }, [theme]); // Depend on theme

  // --- Filter Options ---
  // Make status options dynamic based on showDeleted
  const statusOptions = useMemo(() => {
    const baseOptions = [
      { value: 'all', label: 'All Statuses' },
      { value: 'paid', label: 'Paid' },
      { value: 'unpaid', label: 'Unpaid' },
      { value: 'overdue', label: 'Overdue' },
    ];
    if (filters.showDeleted) {
      baseOptions.push({ value: 'deleted', label: 'Deleted' });
    }
    return baseOptions;
  }, [filters.showDeleted]); // Re-calculate when showDeleted changes

  // Memoized processing and filtering of invoices
  const memoizedProcessedData = useMemo(() => { // START OF useMemo
    if (process.env.NODE_ENV !== 'production') {
      console.log('[InvoiceHistory] Memoizing processed invoices. Filters:', filters, 'allInvoices length:', allInvoices.length, 'URL Search:', location.search);
    }
    // --- Check for Specific IDs from URL ---
    const queryParams = queryString.parse(location.search);
    const invoiceIdsParam = queryParams.invoiceIds;
    const isSpecificIdView = !!invoiceIdsParam; // Check if invoiceIds are in the URL

    let baseFilteredInvoices = allInvoices;

    if (isSpecificIdView) {
      const targetIds = invoiceIdsParam.split(',');
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Memo] Filtering specifically by URL invoiceIds:', targetIds);
      }
      // Filter ONLY by the IDs provided in the URL
      baseFilteredInvoices = allInvoices.filter(inv => targetIds.includes(inv.id));
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[Memo] Filtered down to ${baseFilteredInvoices.length} invoices based on URL IDs.`);
      }
      // In this view, we IGNORE practice, startDate, endDate, period filters initially.
    } else {
      // --- Standard Filtering Logic (when no specific IDs in URL) ---
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Memo] Applying standard filters (Practice, Date/Period).');
      }
      // 1. Initial Data Check
      if (!allInvoices || allInvoices.length === 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[Memo] No allInvoices data to process.');
        }
        return { invoices: [], totals: { paid: 0, unpaid: 0, overdue: 0, count: 0 } };
      }

      // 2. Filter by Practice (if applicable)
      let practiceFiltered = allInvoices;
      if (filters.practice && filters.practice !== 'all') {
        practiceFiltered = allInvoices.filter(inv => inv.practiceId === filters.practice);
        if (process.env.NODE_ENV !== 'production') {
          console.log(`[Memo] Filtered by practice ${filters.practice}: ${practiceFiltered.length} remaining.`);
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[Memo] No practice filter applied (all practices).');
        }
      }

      // 3. Filter by Date Range (if applicable)
      let dateFiltered = practiceFiltered;
      if (filters.startDate && filters.endDate) {
        const start = startOfDay(new Date(filters.startDate)); // Ensure comparison starts at day start
        const end = endOfDay(new Date(filters.endDate));     // Ensure comparison includes whole end day
        dateFiltered = practiceFiltered.filter(inv => {
          const createdAt = inv.createdAt instanceof Date ? inv.createdAt : (inv.createdAt?.seconds ? new Date(inv.createdAt.seconds * 1000) : null);
          return createdAt && createdAt >= start && createdAt <= end;
        });
        if (process.env.NODE_ENV !== 'production') {
          console.log(`[Memo] Filtered by date range (${format(start, 'PP')} - ${format(end, 'PP')}): ${dateFiltered.length} remaining.`);
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[Memo] No date range filter applied.');
        }
      }
      baseFilteredInvoices = dateFiltered;
      // --- End Standard Filtering Logic ---
    }

    // --- Apply Status and Deleted Filters (Applied in BOTH scenarios) ---
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Memo] Applying Status and Deleted filters.');
    }
    // 4. Filter by Status
    let statusFiltered = baseFilteredInvoices;
    if (filters.statusFilter !== 'all') {
      statusFiltered = baseFilteredInvoices.filter(inv => {
        if (filters.statusFilter === 'paid') {
          return inv.isPaid && inv.status !== 'deleted';
        }
        if (filters.statusFilter === 'unpaid') {
          return !inv.isPaid && !calculateIsOverdue(inv) && inv.status !== 'deleted'; // Check not overdue too
        }
        if (filters.statusFilter === 'overdue') {
          // NOW ONLY use calculateIsOverdue – it returns false for paid invoices
          return calculateIsOverdue(inv) && inv.status !== 'deleted';
        }
        if (filters.statusFilter === 'deleted') {
          return inv.status === 'deleted';
        }
        return true;
      });
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[Memo] Filtered by status '${filters.statusFilter}': ${statusFiltered.length} remaining.`);
      }
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Memo] No status filter applied (all statuses).');
      }
    }

    // 5. Apply deleted filter (Show/Hide)
    let processedInvoices = [];
    if (filters.showDeleted) {
      // If showing deleted, include everything from the status filter step
      processedInvoices = statusFiltered;
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[Memo] Showing deleted invoices. Count: ${processedInvoices.length}.`);
      }
    } else {
      // If not showing deleted, filter out any with status 'deleted'
      processedInvoices = statusFiltered.filter(inv => inv.status !== 'deleted');
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[Memo] Hiding deleted invoices. Count: ${processedInvoices.length}.`);
      }
    }
    // --- End Status/Deleted Filtering ---


    // --- Final Mapping, Sorting, and Totals Calculation ---
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Memo] Mapping, sorting, and calculating totals.');
    }
    // Map to display format...
    const displayMappedInvoices = processedInvoices.map(inv => {
      const paymentStatus = getPaymentStatusStyle(inv); // Calculate once
      return {
        ...inv,
        // Ensure dates are JS Date objects for reliable processing
        createdAt: inv.createdAt?.seconds ? new Date(inv.createdAt.seconds * 1000) : (inv.createdAt instanceof Date ? inv.createdAt : null),
        paidDate: inv.paidDate?.seconds ? new Date(inv.paidDate.seconds * 1000) : (inv.paidDate instanceof Date ? inv.paidDate : null),
        // Add derived status properties
        isPaid: inv.isPaid || false,
        isOverdue: paymentStatus.isOverdue || false,
        dueDate: paymentStatus.dueDate || null,
        // Ensure practiceDetails is at least an empty object if null/undefined
        practiceDetails: inv.practiceDetails || {},
      };
    })
    .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)); // Sort by creation date descending

    // Calculate totals...
    const totals = displayMappedInvoices.reduce((acc, inv) => {
      // Make sure amount is a number before adding
      const amount = parseFloat(inv.totalAmount) || 0;
      acc.count += 1;
      
      if (inv.isPaid) {
        acc.paid += amount;
      } else {
        acc.unpaid += amount;
        
        // Explicitly calculate if the invoice is overdue using our helper function
        const isOverdue = calculateIsOverdue(inv);
        
        // If overdue, add to overdue total
        if (isOverdue) {
          acc.overdue += amount;
        }
      }
      
      return acc;
    }, { paid: 0, unpaid: 0, overdue: 0, count: 0 });

    if (process.env.NODE_ENV !== 'production') {
      console.log(`[Memo] Final processing complete: ${displayMappedInvoices.length} invoices. Totals:`, totals);
      console.log(`[Memo] View Mode Context: ${isSpecificIdView ? 'Specific IDs from URL' : 'Standard Filter View'}`);
    }

    return { invoices: displayMappedInvoices, totals };
  // Add location.search to dependencies
  }, [allInvoices, filters.practice, filters.startDate, filters.endDate, filters.showDeleted, filters.statusFilter, location.search, getPaymentStatusStyle, calculateIsOverdue]); // END OF useMemo dependencies array

  // Effect to set invoices and totals from memoized data
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[InvoiceHistory] Setting invoices and totals based on memoizedProcessedData.');
    }
    // Use the correct variable name here
    setInvoices(memoizedProcessedData.invoices);
    setSummaryTotals(memoizedProcessedData.totals);
  }, [memoizedProcessedData]); // Depend on the memoized data

  useEffect(() => {
    updatePractices(allInvoices);
  }, [allInvoices, updatePractices]);

  useEffect(() => {
    if (location.state?.deletedInvoiceId) {
      const deletedId = location.state.deletedInvoiceId;
      if (process.env.NODE_ENV !== 'production') {
        console.log('Detected deleted invoice ID from navigation:', deletedId);
      }
      setRecentlyDeletedIds(prev => {
        if (!prev.includes(deletedId)) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Adding deleted invoice ID to tracking array:', deletedId);
          }
          return [...prev, deletedId];
        }
        return prev;
      });
      setAllInvoices(prevInvoices => 
        prevInvoices.map(invoice => 
          invoice.id === deletedId ? 
            { ...invoice, status: 'deleted' } : 
            invoice
        )
      );
      navigate(location.pathname + location.search, { 
        replace: true, 
        state: {} 
      });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (location.state?.restoredInvoiceId) {
      const restoredId = location.state.restoredInvoiceId;
      if (process.env.NODE_ENV !== 'production') {
        console.log('Detected restored invoice ID from navigation:', restoredId);
      }
      setRecentlyRestoredIds(prev => {
        if (!prev.includes(restoredId)) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Adding restored invoice ID to tracking array:', restoredId);
          }
          return [...prev, restoredId];
        }
        return prev;
      });
      setRecentlyDeletedIds(prev => prev.filter(id => id !== restoredId));
      setAllInvoices(prevInvoices => 
        prevInvoices.map(invoice => 
          invoice.id === restoredId ? 
            { ...invoice, status: 'active' } : 
            invoice
        )
      );
      navigate(location.pathname + location.search, { 
        replace: true, 
        state: {} 
      });
    }
  }, [location, navigate]);

  // SIMPLIFIED: Update forceRefreshData to just reset to defaults and force refresh
  const forceRefreshData = useCallback(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Force refresh requested - resetting to current month defaults.');
    }
    // Reset local state tracking
    setRecentlyDeletedIds([]);
    setRecentlyRestoredIds([]);
    
    // Reset to fresh current month defaults
    setFilters(defaultFilters);
    
    // Force refresh with current month data
    refreshInvoices({ force: true });
  }, [refreshInvoices]);

  const handlePaymentChange = async (invoiceId, checked) => {
    const originalInvoice = allInvoices.find(inv => inv.id === invoiceId);
    if (!originalInvoice) {
      console.error("Original invoice data not found for ID:", invoiceId);
      toast({ title: "Error", description: "Could not find invoice data.", variant: "destructive" });
      return;
    }

    // Use JS Date object for local state and cache update
    const newPaidDate = checked ? new Date() : null;

    try {
      // 1. Update Database (uses serverTimestamp for paidDate if applicable)
      await reportService.updateInvoicePaymentStatus(invoiceId, checked);
      console.log('Database update successful for invoice:', invoiceId, 'Paid:', checked);

      // 2. Update Local State (allInvoices) - IMMEDIATE UI UPDATE
      const updatedAllInvoices = allInvoices.map(inv =>
        inv.id === invoiceId ? {
          ...inv,
          isPaid: checked,
          paidDate: newPaidDate // Store JS Date object or null
        } : inv
      );
      setAllInvoices(updatedAllInvoices);
      console.log('Local state (allInvoices) updated.');

      // 3. Update Memory Cache & Clear LocalStorage Cache via reportService
      if (auth.currentUser?.uid) {
        try {
          // Prepare update data using the JS Date object
          const cacheUpdateData = { isPaid: checked, paidDate: newPaidDate };
          reportService.updateCachedInvoice(auth.currentUser.uid, invoiceId, cacheUpdateData);
          console.log(`Called reportService.updateCachedInvoice for invoice: ${invoiceId}`);
        } catch (cacheError) {
          console.error(`Error calling reportService.updateCachedInvoice:`, cacheError);
          // If updating cache fails, maybe fall back to clearing?
          // reportService.clearCache(auth.currentUser.uid);
        }
      } else {
        console.warn('Could not update cache: User ID not available.');//
      }

      // 4. Show Success Toast
      toast({
        title: "Success",
        description: `Invoice marked as ${checked ? 'paid' : 'unpaid'}.`,
        variant: "success",
      });

    } catch (err) {
      console.error("Error updating payment status:", err);
      toast({
        title: "Error",
        description: "Failed to update payment status. Please try again.",
        variant: "destructive",
      });
      // Consider reverting local state on error if needed, but current approach doesn't require it.
    }
  };

  // --- Restore Invoice Handler ---
  const handleRestoreInvoice = async (invoiceId) => {
    console.log(`[InvoiceHistory] Attempting to restore invoice: ${invoiceId}`);
    try {
      const newStatus = 'unpaid'; // Or 'active' depending on your system's non-deleted status
      console.log(`[InvoiceHistory] Restoring invoice ${invoiceId} to status: ${newStatus}`);

      // 1. Update Database
      await invoiceService.updateInvoiceStatus(invoiceId, newStatus);
      console.log(`[InvoiceHistory] Successfully updated status for invoice ${invoiceId} in Firestore.`);

      // 2. Optimistic UI Update: Update local state (allInvoices)
      setAllInvoices(prevInvoices =>
        prevInvoices.map(inv =>
          inv.id === invoiceId ? { ...inv, status: newStatus, isPaid: false } : inv // Also ensure isPaid is false if restoring to unpaid
        )
      );
      console.log(`[InvoiceHistory] Optimistically updated local state for invoice ${invoiceId}.`);

      // 3. Show Success Toast
      toast({
        title: "Invoice Restored",
        description: `Invoice ${invoiceId.substring(0, 6)} has been restored.`,
        variant: "success",
      });

      // 4. Refresh data from server (optional, but good for consistency)
      // This will fetch the latest data, including the restored invoice.
      // The force: true option helps ensure cache is bypassed if reportService has its own.
      console.log(`[InvoiceHistory] Triggering refresh after restoring invoice ${invoiceId}.`);
      // Pass options object with force: true
      await refreshInvoices({ force: true }); 

    } catch (error) {
      // Log the detailed error object from the service call
      console.error(`[InvoiceHistory] Error restoring invoice ${invoiceId}:`, error);
      // Check for specific Firestore error codes if available
      const errorMessage = error.message || "Could not restore the invoice. Check console for details.";
      if (error.code) {
        console.error(`[InvoiceHistory] Firestore error code: ${error.code}`);
      }

      toast({
        title: "Error Restoring Invoice",
        description: errorMessage,
        variant: "destructive",
      });
      // Optionally revert optimistic update here if needed, though refreshInvoices might correct it.
      // setError(`Failed to restore invoice ${invoiceId}. See console.`);
    }
  };

  const handleEmailReminder = (invoice) => {
    // Find the full invoice details from allInvoices if needed, e.g., for practice email
    const fullInvoiceData = allInvoices.find(inv => inv.id === invoice.id);
    const practiceName = fullInvoiceData?.practiceDetails?.prac_name || invoice.practice || 'the practice';
    const userName = auth.currentUser?.displayName || "[Your Name]"; // Get user's name
    // Ideally, you'd have the practice email stored: const practiceEmail = fullInvoiceData?.practiceDetails?.email || '';

    const subject = `Invoice Reminder: ${invoice.invoiceNumber}`;
    // Updated body to include user's name
    const body = `Hi ${practiceName},\n\nThis is a friendly reminder that invoice ${invoice.invoiceNumber} for £${parseFloat(invoice.totalAmount || 0).toFixed(2)} is overdue.\n\nPlease arrange payment at your earliest convenience.\n\nThanks,\n${userName}`;

    // Use the practice email if available, otherwise leave 'to' empty
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // const mailtoLink = `mailto:${practiceEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`; // If email is available

    window.location.href = mailtoLink;
  };

  // --- Navigation Handler ---
  const handleRowClick = (e, invoiceId) => {
    // Prevent navigation if the click was on an interactive element (button, checkbox, link)
    // Check target and its parents for interactive roles or tags
    let targetElement = e.target;
    while (targetElement && targetElement !== e.currentTarget) {
      if (
        targetElement.tagName === 'BUTTON' ||
        targetElement.tagName === 'INPUT' ||
        targetElement.tagName === 'A' ||
        targetElement.getAttribute('role') === 'button' ||
        targetElement.getAttribute('role') === 'checkbox' ||
        targetElement.closest('[data-interactive="true"]') // Add a data attribute to interactive elements if needed
      ) {
        console.log('[InvoiceHistory] Click on interactive element ignored for row navigation.');
        return; // Stop processing the row click
      }
      targetElement = targetElement.parentElement;
    }

    console.log(`[InvoiceHistory] Row clicked, navigating to invoice: ${invoiceId}`);
    navigate(`${ROUTES.INVOICE_DETAILS.replace(':id', '')}${invoiceId}`); // Use imported ROUTES
  };

  // Modified to support different date formats for mobile/desktop
  const formatFirestoreDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      const date = dateValue.seconds ? new Date(dateValue.seconds * 1000) : new Date(dateValue);
      // Return both formats for responsive display
      return {
        short: format(date, 'dd/MM/yy'), // Short format for mobile
        long: format(date, 'PP') // Original format for desktop
      };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { short: 'Invalid', long: 'Invalid Date' };
    }
  };

  // Base classes for the span badge - Made theme-aware for base text/border
  const badgeBaseClasses = cn(
    "inline-block px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap",
    theme === 'light' ? 'border-gray-300' : 'border-transparent'
  );

  // Helper to format currency
  const formatCurrency = (amount) => {
    // Ensure amount is a number with parseFloat, default to 0 if not convertible
    const numericAmount = parseFloat(amount);
    return `£${(isNaN(numericAmount) ? 0 : numericAmount).toFixed(2)}`;
  };

  // Effect to check for updates on navigation back AND when allInvoices changes
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
    console.log(`[InvoiceHistory] Navigation/Data effect triggered. Key: ${location.key}, allInvoices length: ${allInvoices.length}`);
    }
    // *** ADD GUARD CLAUSE ***
    // If allInvoices isn't populated yet, don't process sessionStorage.
    // The effect will re-run when allInvoices updates.
    if (allInvoices.length === 0) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[InvoiceHistory] Skipping sessionStorage check as allInvoices is empty.');
      }
      return;
    }
    try {
      const updateInfoRaw = sessionStorage.getItem('invoiceUpdateInfo');
      if (process.env.NODE_ENV !== 'production') {
        console.log('[InvoiceHistory] Read from sessionStorage:', updateInfoRaw);
      }
      if (updateInfoRaw) {
        const updateInfo = JSON.parse(updateInfoRaw);
        if (process.env.NODE_ENV !== 'production') {
          console.log('[InvoiceHistory] Found update info in sessionStorage:', updateInfo);
        }
        setAllInvoices(prevAllInvoices => {
           if (process.env.NODE_ENV !== 'production') {
             console.log(`[InvoiceHistory] setAllInvoices callback executing. Prev length: ${prevAllInvoices.length}. Updating ID: ${updateInfo.id}`);
           }
           const updated = prevAllInvoices.map(inv => {
              if (inv.id === updateInfo.id) {
                if (process.env.NODE_ENV !== 'production') {
                  console.log(`[InvoiceHistory] Mapping: Found and updating invoice ${inv.id}.`);
                }
                return {
                  ...inv,
                  isPaid: updateInfo.isPaid,
                  paidDate: updateInfo.paidDate ? new Date(updateInfo.paidDate) : null
                };
              }
              return inv;
           });
           const foundInUpdated = updated.some(inv => inv.id === updateInfo.id && inv.isPaid === updateInfo.isPaid);
           if (process.env.NODE_ENV !== 'production') {
             console.log(`[InvoiceHistory] Item ${updateInfo.id} updated in new array: ${foundInUpdated}`);
           }
           return updated;
        });
        sessionStorage.removeItem('invoiceUpdateInfo');
        if (process.env.NODE_ENV !== 'production') {
          console.log('[InvoiceHistory] Queued setAllInvoices update and removed item from sessionStorage.');
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[InvoiceHistory] No update info found in sessionStorage.');
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("[InvoiceHistory] Error reading or processing sessionStorage:", e);
      }
      sessionStorage.removeItem('invoiceUpdateInfo');
    }
  }, [location.key, allInvoices]); // <-- ADD allInvoices to dependencies

  // Add logging before return to check filter state used for rendering
  if (process.env.NODE_ENV !== 'production') {
    console.log('[InvoiceHistory] Rendering with filters:', filters);
  }

  // --- Render Invoice Item ---
  // This function will render each row in the table
  const renderInvoiceItem = (invoice) => {
    const isDeleted = invoice.status === 'deleted';
    // Calculate isOverdue explicitly here
    const isOverdue = !invoice.isPaid && calculateIsOverdue(invoice);
    // Use correct function signature
    const paymentStatusStyle = getPaymentStatusStyle({...invoice, isOverdue});
    const formattedDate = formatFirestoreDate(invoice.createdAt);

    return (
      <TableRow
        key={invoice.id}
        className={cn(
          "transition-colors duration-150 cursor-pointer", // Base classes
          // Consistent style for deleted rows (background + opacity)
          isDeleted && (theme === 'light' ? 'bg-red-50 opacity-60' : 'bg-red-900/30 opacity-60'),
          // Hover effect for deleted rows
          isDeleted && (theme === 'light' ? 'hover:bg-red-100 hover:opacity-70' : 'hover:bg-red-900/40 hover:opacity-70'),
          // Standard hover effect for non-deleted rows
          !isDeleted && (theme === 'light' ? 'hover:bg-purple-50' : 'hover:bg-purple-900/20')
        )}
        onClick={(e) => handleRowClick(e, invoice.id)}
      >
        {/* Date: Show different formats based on screen size - SMALLER FONT */}
        <TableCell className={cn("py-1 px-0.5 sm:px-2 text-[9px] sm:text-xs whitespace-nowrap w-[60px] sm:w-auto", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
          <span className="hidden sm:inline">{formattedDate.long}</span>
          <span className="inline sm:hidden">{formattedDate.short}</span>
        </TableCell>

        {/* Invoice Number - SMALLER FONT */}
        <TableCell className={cn("py-1 px-0.5 sm:px-2 text-[9px] sm:text-xs font-medium whitespace-nowrap w-[60px] sm:w-auto", theme === 'light' ? 'text-gray-800' : 'text-white')}>
          {invoice.invoiceNumber || invoice.id.substring(0, 6)}
        </TableCell>

        {/* Practice: Allow wrapping instead of truncating - SMALLER FONT */}
        <TableCell className={cn("py-1 px-0.5 sm:px-2 text-[9px] sm:text-xs break-words min-w-[80px] max-w-[100px] sm:max-w-none", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>
          {invoice.practiceDetails?.prac_name || invoice.prac_name || 'N/A'}
        </TableCell>

        {/* Amount - SMALLER FONT */}
        <TableCell className={cn("py-1 px-0.5 sm:px-2 text-[9px] sm:text-xs text-right font-medium whitespace-nowrap w-[60px] sm:w-auto", theme === 'light' ? 'text-gray-800' : 'text-white')}>
          {formatCurrency(invoice.totalAmount)}
        </TableCell>

        {/* Status: Smaller text on mobile */}
        <TableCell className="py-1 px-0.5 sm:px-2 text-center whitespace-nowrap w-[60px] sm:w-auto">
          <Badge variant="outline" className={cn(paymentStatusStyle, "text-[8px] sm:text-xs py-0 sm:py-0.5 px-1 sm:px-2")}>
            {isDeleted
              ? 'Deleted'
              : (invoice.isPaid
                ? 'Paid'
                : isOverdue  // use the local isOverdue variable
                  ? 'Overdue'
                  : 'Unpaid'
                )}
          </Badge>
        </TableCell>
        
        {/* Actions Cell - Further reduced padding */}
        <TableCell className="py-0.5 px-0 sm:px-1 text-center whitespace-nowrap w-[70px] sm:w-auto">
          {/* Tighter spacing for action buttons */}
          <div className="flex items-center justify-center space-x-0">
            {/* Checkbox */}
            <div className={cn("flex items-center justify-center mr-0.5", isDeleted ? 'invisible' : 'visible')} title={invoice.isPaid ? "Mark as Unpaid" : "Mark as Paid"}>
              <Checkbox
                checked={invoice.isPaid}
                onCheckedChange={(checked) => handlePaymentChange(invoice.id, checked)}
                aria-label={`Mark invoice ${invoice.invoiceNumber || invoice.id} as ${invoice.isPaid ? 'unpaid' : 'paid'}`}
                className="h-4 w-4 border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                onClick={(e) => e.stopPropagation()}
                data-interactive="true"
              />
            </div>

            {/* View Button */}
            <div className="flex items-center justify-center">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={(e) => { e.stopPropagation(); navigate(`${ROUTES.INVOICE_DETAILS.replace(':id', '')}${invoice.id}`); }}
                title="View Details"
                data-interactive="true"
              >
                <Eye size={14} />
              </Button>
            </div>

            {/* Mail Button */}
            <div className={cn("flex items-center justify-center", (!isOverdue || isDeleted || invoice.isPaid) ? 'invisible' : 'visible')}>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                onClick={(e) => { e.stopPropagation(); handleEmailReminder(invoice); }}
                title="Send Reminder"
                data-interactive="true"
              >
                <Mail size={14} />
              </Button>
            </div>

            {/* Restore Button */}
            <div className={cn("flex items-center justify-center", !isDeleted ? 'invisible' : 'visible')}>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30"
                onClick={(e) => { e.stopPropagation(); handleRestoreInvoice(invoice.id); }}
                title="Restore Invoice"
                data-interactive="true"
              >
                <RotateCcw size={14} />
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    // Reduce horizontal padding on mobile/small screens, keep container behavior for larger screens
    <div className="container mx-auto px-1 sm:px-2 md:px-4 py-2 sm:py-4 space-y-2 sm:space-y-6 max-w-full">
      {/* Page Title Section - NEW */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <h2 className={cn("text-xl sm:text-2xl font-bold", theme === 'light' ? 'text-black' : 'text-purple-300')}>
            Invoice History
          </h2>
          <HelpIcon sectionKey="InvoiceHistory" />
        </div>
      </div>

      {/* Filters Section - No changes needed here */}
      <Card className={cn("app-card-bg border-border shadow-md")}>
        <CardContent className="p-2 sm:p-4">
          {/* Stack filters vertically on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2 items-end">
            {/* Practice Filter */}
            <div className="mb-2 sm:mb-0">
              <Label htmlFor="practice-filter" className={cn("text-xs font-medium mb-1 block", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Practice</Label>
              <Select value={filters.practice} onValueChange={handlePracticeChange}>
                <SelectTrigger id="practice-filter" className={cn("h-8 text-xs w-full", theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E]')}>
                  <SelectValue placeholder="Select practice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">All Practices</SelectItem>
                  {practices.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Period Filter */}
            <div className="mb-2 sm:mb-0">
              <Label htmlFor="period-filter" className={cn("text-xs font-medium mb-1 block", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Period</Label>
              <Select value={filters.period} onValueChange={handleFilterPeriodChange}>
                <SelectTrigger id="period-filter" className={cn("h-8 text-xs w-full", theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E]')}>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-time" className="text-xs">All Time</SelectItem>
                  <SelectItem value="today" className="text-xs">Today</SelectItem>
                  <SelectItem value="this-week" className="text-xs">This Week</SelectItem>
                  <SelectItem value="this-month" className="text-xs">This Month</SelectItem>
                  <SelectItem value="last-month" className="text-xs">Last Month</SelectItem>
                  <SelectItem value="last-3-months" className="text-xs">Last 3 Months</SelectItem>
                  <SelectItem value="last-6-months" className="text-xs">Last 6 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="mb-2 sm:mb-0">
              <Label htmlFor="statusFilter" className={cn("text-xs font-medium mb-1 block", theme === 'light' ? 'text-gray-700' : 'text-muted-foreground')}>Status</Label>
              <Select value={filters.statusFilter} onValueChange={handleStatusFilterChange}>
                <SelectTrigger id="statusFilter" className={cn("h-8 text-xs w-full", theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#1F1F2E] border-border')}>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className={cn(theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E]')}>
                  {statusOptions.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-xs">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show Deleted & Refresh Button - Improved layout for mobile */}
            <div className="flex flex-row items-end justify-between gap-2 lg:col-span-2">
              {/* Show Deleted Toggle */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="showDeleted"
                  checked={filters.showDeleted}
                  onCheckedChange={handleShowDeletedChange}
                  className="data-[state=checked]:app-accent-purple-bg data-[state=unchecked]:bg-input"
                />
                <Label htmlFor="showDeleted" className={cn("text-xs font-medium whitespace-nowrap", theme === 'light' ? 'text-gray-700' : 'text-muted-foreground')}>
                  Show Deleted
                </Label>
              </div>

              {/* Force Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 text-xs flex items-center gap-1",
                  theme === 'light' ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-border text-muted-foreground hover:bg-muted'
                )}
                onClick={forceRefreshData}
                disabled={loading}
                title="Force refresh invoice list from database"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span className="sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Section - Improve mobile display */}
      {!loading && invoices.length > 0 && (
        <Card className={cn("app-card-bg border-border shadow-md")}>
          <CardContent className="p-2 sm:p-4">
            {/* Maintain 2-col grid on mobile, but adjust font sizes */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              {/* Total Invoices */}
              <div className={cn("p-1.5 sm:p-3 rounded-lg", theme === 'light' ? 'bg-gray-200' : 'bg-[#2A2A3E]')}>
                {/* Reduced font size for label */}
                <h4 className={cn("text-[10px] sm:text-xs font-medium mb-0.5", theme === 'light' ? 'text-gray-700' : 'text-purple-300/80')}>Total Invoices</h4>
                {/* Reduced font size for value */}
                <p className={cn("text-xs sm:text-base font-bold", theme === 'light' ? 'text-black' : 'text-white')}>{summaryTotals.count}</p>
              </div>
              {/* Total Paid */}
              <div className={cn("p-1.5 sm:p-3 rounded-lg", theme === 'light' ? 'bg-green-100' : 'bg-[#2A2A3E]')}>
                {/* Reduced font size for label */}
                <h4 className={cn("text-[10px] sm:text-xs font-medium mb-0.5", theme === 'light' ? 'text-green-700' : 'text-green-400/80')}>Total Paid</h4>
                {/* Reduced font size for value */}
                <p className={cn("text-xs sm:text-base font-bold", theme === 'light' ? 'text-green-900' : 'text-green-300')}>{formatCurrency(summaryTotals.paid)}</p>
              </div>
              {/* Total Unpaid */}
              <div className={cn("p-1.5 sm:p-3 rounded-lg", theme === 'light' ? 'bg-yellow-100' : 'bg-[#2A2A3E]')}>
                {/* Reduced font size for label */}
                <h4 className={cn("text-[10px] sm:text-xs font-medium mb-0.5", theme === 'light' ? 'text-yellow-700' : 'text-yellow-400/80')}>Total Unpaid</h4>
                {/* Reduced font size for value */}
                <p className={cn("text-xs sm:text-base font-bold", theme === 'light' ? 'text-yellow-900' : 'text-yellow-300')}>{formatCurrency(summaryTotals.unpaid)}</p>
              </div>
              {/* Total Overdue */}
              <div className={cn("p-1.5 sm:p-3 rounded-lg", theme === 'light' ? 'bg-red-100' : 'bg-[#2A2A3E]')}>
                {/* Reduced font size for label */}
                <h4 className={cn("text-[10px] sm:text-xs font-medium mb-0.5", theme === 'light' ? 'text-red-700' : 'text-red-400/80')}>Total Overdue</h4>
                {/* Reduced font size for value */}
                <p className={cn("text-xs sm:text-base font-bold", theme === 'light' ? 'text-red-900' : 'text-red-300')}>{formatCurrency(summaryTotals.overdue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice List Section - Using Table - REDUCED PADDING */}
      <Card className={cn("app-card-bg border-border shadow-md px-0")}>
        <CardHeader className="p-1.5 sm:p-4 pb-0">
          <CardTitle className={cn("text-base sm:text-lg", theme === 'light' ? 'text-gray-900' : 'text-accent-foreground')}>Invoice List</CardTitle>
        </CardHeader>
        <CardContent className="p-0"> {/* Remove padding from CardContent to allow table wrapper full control */}
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className={cn("p-4 text-center", theme === 'light' ? 'text-red-600' : 'text-red-400')}>
              {error}
            </div>
          ) : memoizedProcessedData.invoices.length > 0 ? (
            // Remove the negative margin from the table wrapper
            <div className="overflow-x-auto"> {/* This allows horizontal scrolling */}
              <Table>
                <TableHeader>
                  <TableRow className={cn(theme === 'light' ? 'bg-gray-50' : 'bg-gray-800/50')}>
                    {/* Apply minimal padding for headers - SMALLER FONT */}
                    <TableHead className="py-1 px-0.5 sm:px-2 text-[8px] sm:text-xs font-medium uppercase tracking-wider text-left">Date</TableHead>
                    <TableHead className="py-1 px-0.5 sm:px-2 text-[8px] sm:text-xs font-medium uppercase tracking-wider text-left">Invoice #</TableHead>
                    <TableHead className="py-1 px-0.5 sm:px-2 text-[8px] sm:text-xs font-medium uppercase tracking-wider text-left">Practice</TableHead>
                    <TableHead className="py-1 px-0.5 sm:px-2 text-[8px] sm:text-xs font-medium uppercase tracking-wider text-right">Amount</TableHead>
                    <TableHead className="py-1 px-0.5 sm:px-2 text-[8px] sm:text-xs font-medium uppercase tracking-wider text-center">Status</TableHead>
                    {/* Changed from text-right to text-center */}
                    <TableHead className="py-1 px-0 sm:px-1 text-[8px] sm:text-xs font-medium uppercase tracking-wider text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memoizedProcessedData.invoices.map(renderInvoiceItem)}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className={cn("text-center py-6 px-3", theme === 'light' ? 'text-gray-500' : 'text-muted-foreground')}>
              No invoices found matching the selected filters.
            </p>
          )}
        </CardContent>
      </Card>
      {/* ... Toaster ... */}
    </div>
  );
};

// Add a new helper function to determine if an invoice is overdue
const calculateIsOverdue = (invoice) => {
  if (invoice.isPaid) return false;
  
  // Check if there's a specific dueDate field first
  if (invoice.dueDate) {
    const dueDate = invoice.dueDate instanceof Date ? 
      invoice.dueDate : 
      (invoice.dueDate.seconds ? new Date(invoice.dueDate.seconds * 1000) : new Date(invoice.dueDate));
    return isPast(dueDate);
  }
  
  // If no dueDate, calculate based on createdAt + 30 days
  const createdAt = invoice.createdAt instanceof Date ? 
    invoice.createdAt : 
    (invoice.createdAt?.seconds ? new Date(invoice.createdAt.seconds * 1000) : null);
  
  if (!createdAt) return false;
  
  // Invoice is overdue if it's more than 30 days old and not paid
  const thirtyDaysAfterCreation = addDays(createdAt, 30);
  return isPast(thirtyDaysAfterCreation);
};

export default InvoiceHistory;